import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { UserControllerService } from '../../../api-clients/generated/services/AnmcoBackend/api/userController.service';
import { UserResponseDTO } from '../../../api-clients/generated/services/AnmcoBackend/model/userResponseDTO';
import { UserUpdateDTO } from '../../../api-clients/generated/services/AnmcoBackend/model/userUpdateDTO';
import { Profile } from './profile.model';

@Injectable({ providedIn: 'root' })
export class ProfileService {

  private readonly profileSubject = new BehaviorSubject<Profile | null>(null);
  readonly profile$ = this.profileSubject.asObservable();

  get currentProfile(): Profile | null {
    return this.profileSubject.getValue();
  }

  constructor(private userController: UserControllerService) {}

  loadProfile(): Promise<Profile> {
    return firstValueFrom(
      this.userController.getUser().pipe(
        map((dto: UserResponseDTO): Profile => this.mapProfile(dto)),
        tap(profile => this.profileSubject.next(profile)),
      ),
    );
  }

  clearProfile(): void {
    this.profileSubject.next(null);
  }

  getProfile(): Observable<Profile> {
    return this.userController.getUser().pipe(
      map((dto: UserResponseDTO): Profile => this.mapProfile(dto)),
      tap(profile => this.profileSubject.next(profile)),
    );
  }

  private mapProfile(dto: UserResponseDTO): Profile {
    return {
      id: dto.id ?? '',
      nome: dto.nome ?? '',
      cognome: dto.cognome ?? '',
      email: dto.email ?? '',
      titolo: dto.titolo,
      professione: dto.professione,
      specializzazione: dto.specializzazione,
      cittaDiResidenza: dto.cittaDiResidenza,
      telefono: dto.telefono,
      codiceFiscale: dto.codiceFiscale,
      dataDiNascita: dto.dataDiNascita,
      provinciaDiNascita: dto.provinciaDiNascita,
      numeroIscrizioneOrdine: dto.numeroIscrizioneOrdine,
      provinciaIscrizioneOrdine: dto.provinciaIscrizioneOrdine,
      nomeOspedale: dto.nomeOspedale,
      fellow: dto.fellow,
      interessiSpecificati: dto.interessiSpecificati,
    };
  }

  deleteAccount(): Observable<void> {
    return this.userController.sendDeletionRequest().pipe(map(() => void 0));
  }

  updateProfile(profile: Profile): Observable<Profile> {
    const dto: UserUpdateDTO = {
      id: profile.id,
      nome: profile.nome,
      cognome: profile.cognome,
      email: profile.email,
      titolo: profile.titolo,
      professione: profile.professione,
      specializzazione: profile.specializzazione,
      cittaDiResidenza: profile.cittaDiResidenza,
      telefono: profile.telefono,
      codiceFiscale: profile.codiceFiscale,
      dataDiNascita: profile.dataDiNascita,
      provinciaDiNascita: profile.provinciaDiNascita,
      numeroIscrizioneOrdine: profile.numeroIscrizioneOrdine,
      provinciaIscrizioneOrdine: profile.provinciaIscrizioneOrdine,
    };
    return this.userController.updateUser(dto).pipe(
      map((res: UserResponseDTO): Profile => this.mapProfile(res)),
    );
  }
}
